import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, Space, Typography, notification } from 'antd';
import axios from 'axios';

import MainLayout from '@/components/Layout/MainLayout';
import useGlobalConfig from '@/store/global';

const { Title, Text } = Typography;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { themeColor } = useGlobalConfig();
  const [api, contextHolder] = notification.useNotification();
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);

  const auth_token = new URLSearchParams(search).get('auth_token');

  const onResetPassword = async (values: any) => {
    if (values.password !== values.repeat_new_password) {
      api.error({
        message: 'Error',
        description: 'The new password and repeat new password do not match',
      });
      return;
    }

    setResetPasswordLoading(true);

    try {
      await axios.post(
        'sessions/reset_password',
        {
          new_password: values.password,
          repeat_new_password: values.repeat_new_password,
        },
        {
          customHeaders: {
            Authorization: `${auth_token}`,
          },
          customError: true,
        }
      );
      api.success({
        message: 'Password reset successful',
        description: 'Navigating to sign in page...',
      });
      setTimeout(() => {
        navigate('/signin');
      }, 3000);
    } catch (error: any) {
      if (error?.status === 401) {
        api.error({
          message: 'Unauthorized',
          description: 'The reset password link is expired or invalid',
        });
      } else {
        api.error({
          message: 'Error',
          description: 'An error occurred while resetting password',
        });
      }
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const onFinish = async (values: any) => {
    setSendEmailLoading(true);
    try {
      await axios.post('sessions/forgot_password', {
        email: values.email,
        reset_password_url: window.location.origin + '/forgot-password',
      });
      api.success({
        message: 'Email sent',
        description: 'Check your email for password reset instructions',
      });
    } catch (error) {
      console.log(error);
      api.error({
        message: 'Error',
        description: 'An error occurred while sending the email',
      });
    } finally {
      setSendEmailLoading(false);
    }
  };

  if (auth_token) {
    return (
      <MainLayout>
        {contextHolder}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
            padding: '20px 0',
          }}
        >
          <Card
            style={{
              width: 450,
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
              borderRadius: 8,
            }}
          >
            <div>
              <Title
                level={2}
                style={{
                  textAlign: 'center',
                  marginBottom: '48px',
                  marginTop: '24px',
                  color: themeColor,
                }}
              >
                Enter new password
              </Title>
              <Form name="new_password" onFinish={onResetPassword} layout="vertical">
                <Form.Item
                  name="password"
                  rules={[{ required: true, message: 'Please input your password!' }]}
                >
                  <Input
                    prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                    placeholder="Enter Your New Password"
                    size="large"
                    type="password"
                  />
                </Form.Item>
                <Form.Item
                  name="repeat_new_password"
                  rules={[{ required: true, message: 'Please confirm your new password!' }]}
                >
                  <Input
                    prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                    placeholder="Confirm Your New Password"
                    size="large"
                    type="password"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={resetPasswordLoading}
                    disabled={resetPasswordLoading}
                    size="large"
                    style={{
                      background: themeColor,
                      border: 'none',
                    }}
                  >
                    Reset Password
                  </Button>
                </Form.Item>
              </Form>

              <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 20 }}>
                <Text style={{ textAlign: 'center', display: 'block' }}>
                  Remember your password?{' '}
                  <Link to="/signin" style={{ fontWeight: 'bold' }}>
                    Sign in
                  </Link>
                </Text>
                <Text style={{ textAlign: 'center', display: 'block' }}>
                  Don't have an account?{' '}
                  <Link to="/signup" style={{ fontWeight: 'bold' }}>
                    Sign up
                  </Link>
                </Text>
              </Space>
            </div>
          </Card>
        </div>
      </MainLayout>
    );
  }
  return (
    <MainLayout>
      {contextHolder}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          padding: '20px 0',
        }}
      >
        <Card
          style={{
            width: 450,
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            borderRadius: 8,
          }}
        >
          <div>
            <Title
              level={2}
              style={{
                textAlign: 'center',
                marginBottom: '48px',
                marginTop: '24px',
                color: themeColor,
              }}
            >
              Forgot Your Password?
            </Title>
            <Form name="forgot_password" onFinish={onFinish} layout="vertical">
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: 'Please input your email!' },
                  { type: 'email', message: 'Please enter a valid email!' },
                ]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                  placeholder="Enter Your Email Address"
                  size="large"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={sendEmailLoading}
                  disabled={sendEmailLoading}
                  style={{
                    background: themeColor,
                    border: 'none',
                  }}
                >
                  Send password reset instructions
                </Button>
              </Form.Item>
            </Form>

            <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 20 }}>
              <Text style={{ textAlign: 'center', display: 'block' }}>
                Remember your password?{' '}
                <Link to="/signin" style={{ fontWeight: 'bold' }}>
                  Sign in
                </Link>
              </Text>
              <Text style={{ textAlign: 'center', display: 'block' }}>
                Don't have an account?{' '}
                <Link to="/signup" style={{ fontWeight: 'bold' }}>
                  Sign up
                </Link>
              </Text>
            </Space>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ForgotPassword;
